
import get from 'lodash/get';

import Config from '../../../config.json';
import { store } from '../../config/configureStore';
import versionInfo from '../../config/version.json';
import * as Actions from '../../actions/Actions';
import ResourcesAPI from '../../services/api/ResourcesApi';

export const STYLE_BUNDLE_SOURCE_TYPES = Object.freeze({
  local: 'LOCAL', // can force local, but also is the default if remote options fail
  preview: 'PREVIEW', // force use preview styles
  latest: 'LATEST', // force use production styles for version
  user: 'USER', // user based style option, isTestUser will try for preview first, then follow the same path as non-test users, trying production, then local styles
});

const localStyleDetails = Object.freeze({
  version: -1,
  dependency: STYLE_BUNDLE_SOURCE_TYPES.local,
  preview: false,
});

const trimmedVersionNumber = versionInfo.version;
const getStylePath = () => ['assets', 'web', 'style', 'json', `?v=${trimmedVersionNumber}`];
const getPreviewPath = () => ['assets', 'app', 'style', 'preview', 'json', `?v=${trimmedVersionNumber}`];

const setIsAvailable = () => store.dispatch(Actions.setIsFetchedRemoteStylesAvailable(true));
const setRemoteStyles = remoteStyles => store.dispatch(Actions.setRemoteStyles(remoteStyles));

const getRemoteJSON = async (url) => {
  const config = { method: 'GET' };
  const response = await fetch(url, config);
  if (!response.ok) throw new Error(response.statusText);
  const data = response.json(); // get JSON from the response
  return data; // returns a promise, which resolves to this data value
};

class RemoteStyle {
  values = {};

  styleBundleDetails = {
    ...localStyleDetails,
  };

  isReady = false;

  static instance = null;

  static getInstance() {
    if (RemoteStyle.instance == null) {
      RemoteStyle.instance = new RemoteStyle();
    }
    return this.instance;
  }

  downloadFromDatabase(user, path, styleType, actionOnFailure) {
    console.log(`attempting to get ${styleType}`);
    ResourcesAPI.getAllResources(user, path).then((res) => {
      const pathToStyleFile = get(res, 'data.href');
      if (pathToStyleFile) {
        // get the list of versions in the GCP bucket
        getRemoteJSON(pathToStyleFile)
          .then((styleJson) => {
            if (styleJson) {
              console.log(`using ${styleType}.`);
              const styleBundleDetails = res.data;
              this.styleBundleDetails.version = styleBundleDetails.version;
              this.styleBundleDetails.dependency = styleBundleDetails.dependency;
              this.styleBundleDetails.preview = styleBundleDetails.preview;
              this.isReady = true;
              setRemoteStyles(styleJson);
              setIsAvailable(true);
            }
          })
          .catch((err) => {
            console.log(`unable to retrieve remote style files: ${err}`);
            setIsAvailable(false);
          });
      } else {
        console.log('cannot parse data');
      }
    }).catch((err) => {
      console.log('unable to download remote style: ', err);
      if (actionOnFailure) {
        actionOnFailure();
      }
    });
  }

  useLocalStyles = () => console.log('use local styles');

  downloadStyleFile(user) {
    return this.downloadFromDatabase(user, getStylePath(), 'remote styles', this.useLocalStyles);
  }

  downloadPreviewStyleFile(user) {
    this.downloadFromDatabase(user, getPreviewPath(), 'remote preview styles', () => this.downloadStyleFile(user));
  }

  async downloadFile(user = {}) {
    // invalidate styles before they are fetched again
    setIsAvailable(false);
    const { styleSource } = Config.general;
    if (styleSource === STYLE_BUNDLE_SOURCE_TYPES.latest) {
      // force using remote styles, do not use user properties
      this.downloadStyleFile(null);
    } else if (styleSource === STYLE_BUNDLE_SOURCE_TYPES.user) {
      // if isTestUser is true, return a preview if there is one (handled by api), if not, return the regular style file
      this.downloadStyleFile(user);
    } else if (styleSource === STYLE_BUNDLE_SOURCE_TYPES.preview) {
      // attempt to force to get the preview style, if not, returns the main style file, if no luck, use local styles
      this.downloadPreviewStyleFile(null);
    } else {
      // otherwise, just use the local styles.
      this.useLocalStyles();
    }
  }
}

export const getRemoteStyle = (styleBundleLocation) => {
  const RemoteStyleInstance = RemoteStyle.getInstance();
  if (RemoteStyleInstance.isReady) {
    return get(RemoteStyleInstance.values, styleBundleLocation);
  }
};

export const getRemoteStyleInfo = () => {
  const RemoteStyleInstance = RemoteStyle.getInstance();
  if (RemoteStyleInstance.isReady) {
    return RemoteStyleInstance.styleBundleDetails;
  }
  return localStyleDetails;
};

export const RemoteStylesClass = RemoteStyle;
